import React, { useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';
import { ExternalLink, ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import * as pdfjs from 'pdfjs-dist';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const SimplePDFViewer = ({ pdfPath, onOpenFullPdf }) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfWidth, setPdfWidth] = useState(400);

  // Calculate responsive PDF width
  useEffect(() => {
    const updatePdfWidth = () => {
      const containerWidth = window.innerWidth;
      // Mobile: full width minus padding
      if (containerWidth < 768) {
        setPdfWidth(containerWidth - 48); // 24px padding on each side
      }
      // Tablet: 80% of container
      else if (containerWidth < 1024) {
        setPdfWidth(Math.min(containerWidth * 0.8, 600));
      }
      // Desktop: max 500px
      else {
        setPdfWidth(500);
      }
    };

    updatePdfWidth();
    window.addEventListener('resize', updatePdfWidth);
    return () => window.removeEventListener('resize', updatePdfWidth);
  }, []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log(`PDF loaded successfully: ${pdfPath}, pages: ${numPages}`);
    setNumPages(numPages);
  };

  const onDocumentLoadError = (error) => {
    console.error(`PDF load error for ${pdfPath}:`, error);
  };

  const nextPage = () => {
    setCurrentPage(prev => prev < numPages ? prev + 1 : 1);
  };

  const prevPage = () => {
    setCurrentPage(prev => prev > 1 ? prev - 1 : numPages);
  };

  // Watermark component
  const Watermark = () => (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 1 }}>
      <div className="absolute inset-0">
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className="absolute text-purple-400 font-bold text-lg transform -rotate-45"
            style={{
              left: `${(i * 30) % 100}%`,
              top: `${(i * 20) % 100}%`,
              opacity: 0.15,
              animation: `watermarkFloat ${4 + (i % 2)}s ease-in-out infinite`
            }}
          >
            HyperHumane
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="border rounded-lg p-3 sm:p-6 bg-white relative w-full overflow-hidden">
      {/* Page indicator in top right */}
      {numPages && (
        <div className="absolute top-2 right-2 sm:top-4 sm:right-4 bg-white/90 px-2 py-1 rounded text-xs text-purple-800 font-medium z-10">
          {currentPage} of {numPages}
        </div>
      )}
      
      <div className="relative flex justify-center p-2 sm:p-4 w-full">
        {/* Left arrow */}
        {numPages && numPages > 1 && (
          <button
            onClick={prevPage}
            disabled={currentPage <= 1}
            className="absolute left-1 sm:left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 p-1 sm:p-2 rounded-full shadow-lg transition-all duration-200 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} className="sm:w-5 sm:h-5" />
          </button>
        )}
        
        {/* Right arrow */}
        {numPages && numPages > 1 && (
          <button
            onClick={nextPage}
            className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-purple-700 p-1 sm:p-2 rounded-full shadow-lg transition-all duration-200 z-10"
          >
            {currentPage === numPages ? 
              <RotateCcw size={16} className="sm:w-5 sm:h-5" /> : 
              <ChevronRight size={16} className="sm:w-5 sm:h-5" />
            }
          </button>
        )}
        
        <div className="p-2 sm:p-4 w-full relative flex justify-center">
          <Document
            file={pdfPath}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={<div className="text-purple-600">Loading PDF...</div>}
            error={<div className="text-red-600">Failed to load PDF</div>}
          >
            <Page
              pageNumber={currentPage}
              width={pdfWidth}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="max-w-full h-auto"
            />
          </Document>
          <Watermark />
        </div>
      </div>
      
      {/* Expand Presentation button */}
      {onOpenFullPdf && (
        <button
          onClick={onOpenFullPdf}
          className="mt-4 w-full bg-purple-100 text-purple-800 hover:bg-purple-200 px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <ExternalLink size={16} />
          Expand Presentation
        </button>
      )}

      {/* Watermark animation CSS */}
      <style jsx>{`
        @keyframes watermarkFloat {
          0%, 100% { opacity: 0.15; transform: translateY(0px) rotate(-45deg); }
          50% { opacity: 0.25; transform: translateY(-2px) rotate(-45deg); }
        }
      `}</style>
    </div>
  );
};

export default SimplePDFViewer; 